import React, { createContext, useState, useContext, useCallback, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { useAuth } from './AuthContext';
import { useUserRole } from './UserRoleContext';
import agentApi from '../services/agentApi';
import callApi from '../services/callApi';
import dialerApi from '../services/dialerApi';
import campaignApi from '../services/campaignApi';
import { addCallListener } from '../services/sipService';
import { useSocketContext } from './SocketContext';
import { mapFrontendStatusToBackend } from '../utils/statusMapping';

const AVAILABILITY_WORDS = new Set(['available', 'busy', 'away', 'offline']);

const readPersistedAvailability = (payload) => {
  if (!payload || typeof payload !== 'object') return null;
  const nested = payload.data && typeof payload.data === 'object' ? payload.data : null;
  const candidates = [
    payload.availability_status,
    payload.availabilityStatus,
    nested?.availability_status,
    nested?.availabilityStatus,
    payload.status,
    nested?.status,
  ];

  for (const candidate of candidates) {
    const word = String(candidate ?? '').toLowerCase();
    if (AVAILABILITY_WORDS.has(word)) return word;
  }

  return null;
};

const displayForPersistedAvailability = (word) => {
  if (word === 'busy') return 'Busy';
  if (word === 'away') return 'Away';
  if (word === 'available') return 'available';
  if (word === 'offline') return 'offline';
  return null;
};

const DialerContext = createContext();

export const useDialerContext = () => useContext(DialerContext);

export const DialerProvider = ({ children }) => {
  const { lastAgentStatus, socket, isConnected } = useSocketContext();
  const { user } = useAuth();
  // Get current user role for role-specific behaviors
  const { role, hasPermission } = useUserRole();

  // States previously in SidebarDialer (or similar)
  const [agentStatus, setAgentStatus] = useState('available'); // Available, Unavailable, On Call, Wrap-up, Offline etc.
  // Where the latest persisted status write stands: idle | saving | saved |
  // error. The availability pill confirms a change with it.
  const [agentStatusSave, setAgentStatusSave] = useState('idle');
  const agentStatusRef = useRef(agentStatus);
  const statusRevisionRef = useRef(0);
  const pendingBackendStatusRef = useRef(null);
  const statusWriteActiveRef = useRef(false);
  const availabilityUserIdRef = useRef(null);
  const availabilityReadCancelRef = useRef(null);
  const availabilityRenderUserIdRef = useRef(user?.id);
  const availabilityRenderConnectedRef = useRef(isConnected === true);
  const socketConnectedRef = useRef(false);
  const socketHadConnectionRef = useRef(false);
  const [currentCallInfo, setCurrentCallInfo] = useState(null); // { id, name, number, status: 'live'/'connecting'/'ended' }
  const [inActiveCampaign, setInActiveCampaign] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(-1); // index of selected campaign -1 or selected campaign index
  const [isMuted, setIsMuted] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [isInCall, setIsInCall] = useState(false);

  // Barge-in functionality states
  const [bargeRequests, setBargeRequests] = useState([]); // [{id, fromAgent, toAgent, status, timestamp}]
  const [activeBarge, setActiveBarge] = useState(null); // Current active barge session
  const [pendingBargeRequest, setPendingBargeRequest] = useState(null); // For the closer to see incoming request

  // Call monitoring states (for managers/admins)
  const [monitoredCalls, setMonitoredCalls] = useState([]); // List of calls being monitored
  const [isMonitoringCall, setIsMonitoringCall] = useState(false); // Whether currently monitoring
  const [monitorMode, setMonitorMode] = useState('listen'); // 'listen', 'whisper', 'barge'

  const [campaigns, setCampaigns] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [campaignsError, setCampaignsError] = useState(null);
  const [campaignMembershipLoading, setCampaignMembershipLoading] = useState(false);

  const refreshCampaigns = useCallback(async () => {
    if (!user) {
      setCampaigns([]);
      setSelectedCampaign(-1);
      setInActiveCampaign(false);
      return [];
    }

    setCampaignsLoading(true);
    setCampaignsError(null);

    try {
      const response = await campaignApi.getAll();
      const nextCampaigns = Array.isArray(response.data) ? response.data : [];
      const activeIndex = nextCampaigns.findIndex(
        (campaign) => campaign.assignment?.isActive === true
      );

      setCampaigns(nextCampaigns);
      setInActiveCampaign(activeIndex >= 0);
      setSelectedCampaign((currentIndex) => {
        if (activeIndex >= 0) return activeIndex;
        if (currentIndex >= 0 && currentIndex < nextCampaigns.length) return currentIndex;
        return nextCampaigns.length > 0 ? 0 : -1;
      });
      return nextCampaigns;
    } catch (error) {
      setCampaignsError(error.message || 'Failed to load assigned campaigns');
      throw error;
    } finally {
      setCampaignsLoading(false);
    }
  }, [user?.id]);

  // Fetch assignments only when authenticated. The active membership returned
  // by the server is the source of truth after login and refresh.
  useEffect(() => {
    refreshCampaigns().catch(() => {
      toast.error('Failed to load assigned campaigns');
    });
  }, [refreshCampaigns]);

  // Persisted availability after refresh and after a dropped socket reconnects.
  // The first connect is not a second read, and it must not drop the mount read.
  // A choice or in-flight write wins over a late row.
  availabilityRenderUserIdRef.current = user?.id;
  availabilityRenderConnectedRef.current = isConnected === true;
  useEffect(() => {
    if (!user?.id) {
      availabilityReadCancelRef.current?.();
      availabilityReadCancelRef.current = null;
      availabilityUserIdRef.current = null;
      socketConnectedRef.current = isConnected === true;
      socketHadConnectionRef.current = false;
      return undefined;
    }

    const previouslyConnected = socketConnectedRef.current;
    const hadConnection = socketHadConnectionRef.current;
    const userChanged = availabilityUserIdRef.current !== user.id;
    const reconnected =
      isConnected === true && !previouslyConnected && hadConnection && !userChanged;

    socketConnectedRef.current = isConnected === true;
    if (isConnected === true) socketHadConnectionRef.current = true;
    availabilityUserIdRef.current = user.id;

    if (userChanged || reconnected) {
      availabilityReadCancelRef.current?.();

      let cancelled = false;
      availabilityReadCancelRef.current = () => {
        cancelled = true;
      };

      const revisionAtRead = statusRevisionRef.current;
      const writeInFlightAtRead =
        statusWriteActiveRef.current || pendingBackendStatusRef.current != null;

      agentApi
        .getMyStatus()
        .then((payload) => {
          if (cancelled || statusRevisionRef.current !== revisionAtRead) return;
          if (writeInFlightAtRead) return;
          if (statusWriteActiveRef.current || pendingBackendStatusRef.current != null) return;
          const label = displayForPersistedAvailability(readPersistedAvailability(payload));
          if (!label) return;
          agentStatusRef.current = label;
          setAgentStatus(label);
        })
        .catch(() => {});
    }

    return () => {
      const nextUserId = availabilityRenderUserIdRef.current;
      const nextConnected = availabilityRenderConnectedRef.current;
      const firstConnectKeepsRead =
        nextUserId === user.id &&
        nextConnected &&
        isConnected !== true &&
        !socketHadConnectionRef.current;

      if (firstConnectKeepsRead) return;

      availabilityReadCancelRef.current?.();
      availabilityReadCancelRef.current = null;
    };
  }, [user?.id, isConnected]);


  // Active agents populated from Phoenix Presence — keyed by user_id
  const [presenceState, setPresenceState] = useState({});

  // Sync presenceState from socket events (agent:init snapshot + presence_diff patches)
  useEffect(() => {
    if (!lastAgentStatus) return;

    // agent:init sends full presence snapshot: { presence: { user_id: { metas: [...] } } }
    if (lastAgentStatus.presence && !lastAgentStatus.presenceDiff) {
      setPresenceState(lastAgentStatus.presence);
      return;
    }

    // presence_diff patches: { joins: { user_id: { metas: [...] } }, leaves: { user_id: {...} } }
    if (lastAgentStatus.presenceDiff) {
      const { joins = {}, leaves = {} } = lastAgentStatus.presenceDiff;
      setPresenceState(prev => {
        const next = { ...prev };
        Object.entries(joins).forEach(([id, data]) => { next[id] = data; });
        Object.keys(leaves).forEach(id => { delete next[id]; });
        return next;
      });
    }
  }, [lastAgentStatus]);

  // Derive flat activeAgents array from presence state for consumers
  const activeAgents = Object.entries(presenceState).map(([id, data]) => {
    const meta = (data.metas || [])[0] || {};
    return {
      id,
      name: meta.name || 'Unknown',
      role: meta.role || 'agent',
      status: meta.status || 'offline',
      currentCall: meta.current_call || null,
    };
  });

  // callStatus can be outgoing, incoming, progress(meaning ringing), accepted, confirmed, ended, muted
    const [callStatus, setCallStatus] = useState(null);

    useEffect(() => {
      const callListener = (event, data) => {
                setCallStatus(event)
      };

      const removeListener = addCallListener(callListener);

      return () => {
        if (removeListener) removeListener();
      };
    }, [addCallListener]);
  // Duration timer effect
  useEffect(() => {
    let timer;
    if (callStatus!=null && (callStatus=="accepted" || callStatus=="confirmed" || callStatus=="muted")) {
      setIsInCall(true);
      timer = setInterval(() => {
        setCallDuration(prev => prev + 1);
      }, 1000);
    } else {
      setCallDuration(0);
      setIsInCall(false);
    }
    return () => clearInterval(timer);
  }, [callStatus]);

  // --- Call Control Actions ---

  // One REST write at a time, in choice order. The server fans the committed
  // row out to live Presence; a parallel channel write can publish a stale alias.
  const pumpStatusWrite = useCallback(() => {
    if (statusWriteActiveRef.current || pendingBackendStatusRef.current == null) return;

    const backendStatus = pendingBackendStatusRef.current;
    const revision = statusRevisionRef.current;
    pendingBackendStatusRef.current = null;
    statusWriteActiveRef.current = true;

    setAgentStatusSave('saving');
    Promise.resolve()
      .then(() => agentApi.updateMyStatus(backendStatus))
      .then(() => {
        if (revision === statusRevisionRef.current && pendingBackendStatusRef.current == null) {
          setAgentStatusSave('saved');
        }
      })
      .catch(() => {
        if (revision === statusRevisionRef.current && pendingBackendStatusRef.current == null) {
          setAgentStatusSave('error');
          toast.error('Failed to update status');
        }
      })
      .finally(() => {
        statusWriteActiveRef.current = false;
        pumpStatusWrite();
      });
  }, []);

  const updateAgentStatus = useCallback((newStatus, { persist = true } = {}) => {
    statusRevisionRef.current += 1;
    agentStatusRef.current = newStatus;
    setAgentStatus(newStatus);
    // Predictive ownership is committed by the engine. Project its lifecycle
    // locally without writing a released assignment back to busy.
    pendingBackendStatusRef.current = persist ? mapFrontendStatusToBackend(newStatus) : null;
    if (persist) pumpStatusWrite();
  }, [pumpStatusWrite]);

  // Start a call (e.g., from predictive dialer event or manual dial)
  const startCall = useCallback((phoneNumber, metadata = {}) => {
    const callInfo = {
      id: metadata.callId,
      number: phoneNumber,
      startTime: new Date(),
      ...metadata,
      status: metadata.status || 'connecting'
    };
    setCurrentCallInfo(callInfo);
    const nextStatus = callInfo.status === 'live' ? 'On Call' : 'Connecting';
    // Integrated dialer publishes Connecting before this call. Skip that repeat;
    // a direct start still publishes through updateAgentStatus.
    if (agentStatusRef.current !== nextStatus) {
      updateAgentStatus(nextStatus);
    }
  }, [updateAgentStatus]);

  // End a call
  const endCall = useCallback(async () => {
    if (!currentCallInfo) return false;

    const callId = currentCallInfo.id || currentCallInfo.callId;
    if (!callId) {
      toast.error('The active call is missing its tracked call ID');
      return false;
    }

    try {
      await callApi.complete(callId, { durationSeconds: callDuration });
      updateAgentStatus('Wrap-up');
      setCurrentCallInfo(null);
      setIsMuted(false);

      if (activeBarge) {
        endBargeSession();
      }

      return true;
    } catch (error) {
      toast.error(error.message || 'Failed to end call');
      return false;
    }
  }, [currentCallInfo, callDuration, activeBarge, updateAgentStatus]);

  // Toggle Mute — wired to PUT /calls/:id/mute or /unmute
  const toggleMute = useCallback(() => {
    const callId = currentCallInfo?.id || currentCallInfo?.callId || currentCallInfo?.channelId;
    if (!callId) {
      // No active call ID — nothing to toggle on the backend
      return;
    }
    const newMuteState = !isMuted;
    // Optimistic UI update
    setIsMuted(newMuteState);
    dialerApi.toggleMute({ callId: String(callId), muted: newMuteState })
      .then(() => {})
      .catch(() => {
        // Revert the optimistic UI update on failure
        setIsMuted(prev => !prev);
        toast.error('Failed to toggle mute');
      });
  }, [currentCallInfo, isMuted]);

  // Hold / Unhold — wired to PUT /calls/:id/hold or /unhold.
  // Local state is only updated once the API confirms success; PubSub
  // events in SipContext also sync this via setCurrentCallInfo({ status }).
  const [isOnHold, setIsOnHold] = useState(false);

  const holdCall = useCallback(() => {
    const callId = currentCallInfo?.id || currentCallInfo?.callId;
    if (!callId) return Promise.resolve(false);
    return dialerApi.holdCall(String(callId))
      .then(() => { setIsOnHold(true); return true; })
      .catch((err) => {
        toast.error(err.message || 'Failed to hold call');
        return false;
      });
  }, [currentCallInfo]);

  const unholdCall = useCallback(() => {
    const callId = currentCallInfo?.id || currentCallInfo?.callId;
    if (!callId) return Promise.resolve(false);
    return dialerApi.unholdCall(String(callId))
      .then(() => { setIsOnHold(false); return true; })
      .catch((err) => {
        toast.error(err.message || 'Failed to resume call');
        return false;
      });
  }, [currentCallInfo]);

  // Joining a campaign activates an existing assignment. Leaving keeps the
  // assignment selectable, so the agent can rejoin it after a reload.
  const toggleCampaignActive = useCallback(async () => {
    const campaign = campaigns[selectedCampaign];
    if (!campaign || campaignMembershipLoading) return false;

    setCampaignMembershipLoading(true);
    setCampaignsError(null);

    try {
      if (inActiveCampaign) {
        await campaignApi.deactivateMembership(campaign.id);
      } else {
        await campaignApi.activateMembership(campaign.id);
      }

      await refreshCampaigns();
      return true;
    } catch (error) {
      const message =
        error.message ||
        (inActiveCampaign ? 'Failed to leave campaign' : 'Failed to join campaign');
      setCampaignsError(message);
      toast.error(message);
      return false;
    } finally {
      setCampaignMembershipLoading(false);
    }
  }, [
    campaignMembershipLoading,
    campaigns,
    inActiveCampaign,
    refreshCampaigns,
    selectedCampaign
  ]);

  // --- Barge Functionality ---

  // Request barge (Fronter requesting closer assistance)
  const requestBarge = useCallback((closerId) => {
        if (!currentCallInfo) {
            return false;
    }

    if (!hasPermission('canRequestBarge')) {
            return false;
    }

    // Generate new barge request
    const newRequest = {
      id: Date.now(),
      fromAgent: 'current-user-id', // Would come from auth context
      toAgent: closerId,
      callInfo: currentCallInfo,
      status: 'pending',
      timestamp: new Date().toISOString()
    };

    // Add to barge requests
    setBargeRequests(prev => [...prev, newRequest]);

    // Send notification to the closer via socket
    if (socket) {
      socket.emit('barge:request', { closerId, requestData: newRequest });
          } else {
            // Handle error - maybe show a message to the user?
      // Optionally revert the barge request state if notification fails critically
      setBargeRequests(prev => prev.filter(req => req.id !== newRequest.id));
      return false; // Indicate failure
    }
    // This would trigger a notification on the closer's dashboard

    return true;
  }, [currentCallInfo, hasPermission, socket]);

  // Accept barge request (Closer accepting fronter's request)
  const acceptBargeRequest = useCallback((requestId) => {
        if (!hasPermission('canAcceptBarge')) {
            return false;
    }

    // Find the request
    const request = bargeRequests.find(req => req.id === requestId);
    if (!request) {
            return false;
    }

    // Update the request status
    setBargeRequests(prev =>
      prev.map(req =>
        req.id === requestId ? {...req, status: 'accepted'} : req
      )
    );

    // Set active barge session
    setActiveBarge({
      ...request,
      status: 'active',
      joinedAt: new Date().toISOString()
    });

    // Establish the actual call connection via the telephony system (Barge)
    const channelIdToBarge = request?.callInfo?.channelId;
    if (channelIdToBarge) {
      dialerApi.bargeIntoCall({ callId: channelIdToBarge })
        .then(() => {
                  })
        .catch(() => {
          setBargeRequests(prev =>
            prev.map(req =>
              req.id === requestId ? {...req, status: 'pending'} : req
            )
          );
          setActiveBarge(null);
          toast.error('Failed to join call');
        });
    } else {
            // Revert state changes as we can't proceed
      setBargeRequests(prev =>
        prev.map(req =>
          req.id === requestId ? {...req, status: 'pending'} : req
        )
      );
      setActiveBarge(null);
    }
    // This would connect the closer to the active call

    return true;
  }, [bargeRequests, hasPermission]);

  // Reject barge request
  const rejectBargeRequest = useCallback((requestId) => {
        // Update the request status
    setBargeRequests(prev =>
      prev.map(req =>
        req.id === requestId ? {...req, status: 'rejected'} : req
      )
    );

    // Clear the pending request for this closer
    setPendingBargeRequest(null);

    // Notify the fronter that the request was rejected via socket
    const request = bargeRequests.find(req => req.id === requestId);
    if (socket && request && request.fromAgent) { // Ensure we have socket, request, and fronter ID
      socket.emit('barge:reject', {
        fronterId: request.fromAgent, // Assuming fromAgent holds the fronter's ID
        requestId: requestId,
        reason: 'Rejected by closer' // Optional: Add a reason input later
      });
      if (import.meta.env.DEV) console.log(`DialerContext: Emitted barge:reject for request ${requestId} (Fronter: ${request.fromAgent})`);
    } else {
      if (import.meta.env.DEV) console.warn(`DialerContext: Cannot send barge rejection notification for request ${requestId}. Socket unavailable, request not found, or fronter ID missing.`);
      // Consider if failure to notify is critical
    }

    return true;
  }, [socket, bargeRequests]);

  // End barge session
  const endBargeSession = useCallback(() => {
        if (!activeBarge) {
            return false;
    }

    // Update the barge request status
    setBargeRequests(prev =>
      prev.map(req =>
        req.id === activeBarge.id ? {...req, status: 'completed'} : req
      )
    );

    // Clear the active barge
    setActiveBarge(null);

    // Disconnect the closer from the call
    const closerChannelId = activeBarge.closerChannelId; // Assuming this is stored when barge is established
    if (closerChannelId) {
      dialerApi.hangupCall({ channelId: closerChannelId })
        .then(() => {})
        .catch(() => {
          toast.error('Failed to end barge session');
        });
    } else {
            // Continue with UI state updates even if we can't hang up the channel
    }

    return true;
  }, [activeBarge]);

  // --- Call Monitoring (for Managers and Admins) ---

  // Start monitoring a call
  const startMonitoringCall = useCallback((agentId, mode = 'listen') => {
        if (!hasPermission('canMonitorCalls')) {
            return false;
    }

    // Find agent to monitor
    const agent = activeAgents.find(a => a.id === agentId);
    if (!agent || agent.status !== 'On Call') {
            return false;
    }

    setIsMonitoringCall(true);
    setMonitorMode(mode);
    setMonitoredCalls(prev => [...prev, {
      agentId,
      startedAt: new Date().toISOString(),
      mode,
      callInfo: agent.currentCall
    }]);

    // Establish the actual monitoring connection via telephony system
    const channelToMonitor = agent.channelId; // Assuming agent object has channelId property
    if (channelToMonitor) {
      dialerApi.monitorCall({ channelId: channelToMonitor, mode })
        .then(response => {
                    // Store the monitoring channel ID for later use when stopping monitoring
          setMonitoredCalls(prev => {
            const updatedCalls = [...prev];
            if (updatedCalls.length > 0) {
              const lastCall = updatedCalls[updatedCalls.length - 1];
              updatedCalls[updatedCalls.length - 1] = {
                ...lastCall,
                monitoringChannelId: response.monitoringChannelId
              };
            }
            return updatedCalls;
          });
        })
        .catch(() => {
          setIsMonitoringCall(false);
          setMonitoredCalls(prev => prev.slice(0, -1));
          toast.error('Failed to start monitoring');
        });
    } else {
            setIsMonitoringCall(false);
      setMonitoredCalls(prev => prev.slice(0, -1)); // Remove the last added call
      return false;
    }

    return true;
  }, [activeAgents, hasPermission]);

  // Stop monitoring a call
  const stopMonitoringCall = useCallback(() => {
        setIsMonitoringCall(false);

    // Record the end time for the most recent monitored call
    setMonitoredCalls(prev => {
      if (prev.length === 0) return prev;
      const lastCall = {...prev[prev.length - 1], endedAt: new Date().toISOString()};
      return [...prev.slice(0, -1), lastCall];
    });

    // Disconnect the monitoring session
    const lastCall = monitoredCalls.length > 0 ? monitoredCalls[monitoredCalls.length - 1] : null;
    const monitoringChannelId = lastCall?.monitoringChannelId;

    if (monitoringChannelId) {
      dialerApi.hangupCall({ channelId: monitoringChannelId })
        .then(() => {})
        .catch(() => {
          toast.error('Failed to stop monitoring');
        });
    } else {
            // Continue with UI state updates even if we can't hang up the channel
    }

    return true;
  }, [monitoredCalls]);

  // Change monitor mode (listen/whisper/barge)
  const changeMonitorMode = useCallback((mode) => {
        if (!isMonitoringCall) {
            return false;
    }

    setMonitorMode(mode);

    // Update the monitoring mode in the telephony system
    // Since we can't change an existing monitoring session's mode directly in Asterisk,
    // we need to stop the current session and start a new one with the new mode

    // Get the current monitoring session details
    const lastCall = monitoredCalls.length > 0 ? monitoredCalls[monitoredCalls.length - 1] : null;
    const currentChannelId = lastCall?.channelId; // The agent's channel being monitored
    const monitoringChannelId = lastCall?.monitoringChannelId; // Our monitoring channel

    if (!currentChannelId) {
            return false;
    }

    // First, hang up the current monitoring channel if we have one
    if (monitoringChannelId) {
      dialerApi.hangupCall({ channelId: monitoringChannelId })
        .catch(() => {
          // Continue anyway to establish new connection
        });
    }

    // Start a new monitoring session with the new mode
    dialerApi.monitorCall({ channelId: currentChannelId, mode })
      .then(response => {
                // Update the monitoring channel ID
        setMonitoredCalls(prev => {
          const updatedCalls = [...prev];
          if (updatedCalls.length > 0) {
            const lastCall = updatedCalls[updatedCalls.length - 1];
            updatedCalls[updatedCalls.length - 1] = {
              ...lastCall,
              mode, // Update the mode
              monitoringChannelId: response.monitoringChannelId // Update the channel ID
            };
          }
          return updatedCalls;
        });
      })
      .catch(() => {
        toast.error('Failed to change monitor mode');
      });

    return true;
  }, [isMonitoringCall, monitoredCalls]);

  // --- Value provided by context ---
  const value = {
    agentStatusSave,
    // State
    agentStatus,
    currentCallInfo,
    setCurrentCallInfo,
    inActiveCampaign,
    selectedCampaign,
    isMuted,
    isInCall,
    isOnHold,
    setIsOnHold,
    setIsMuted,
    callDuration,
    campaigns,
    campaignsLoading,
    campaignsError,
    campaignMembershipLoading,
    bargeRequests,
    activeBarge,
    pendingBargeRequest,
    activeAgents,
    isMonitoringCall,
    monitorMode,
    monitoredCalls,

    // Call Control Actions
    updateAgentStatus,
    startCall,
    endCall,
    toggleMute,
    holdCall,
    unholdCall,
    setSelectedCampaign,
    toggleCampaignActive,
    refreshCampaigns,

    // Barge Actions
    requestBarge,
    acceptBargeRequest,
    rejectBargeRequest,
    endBargeSession,

    // Monitoring Actions
    startMonitoringCall,
    stopMonitoringCall,
    changeMonitorMode,
  };

  return (
    <DialerContext.Provider value={value}>
      {children}
    </DialerContext.Provider>
  );
};
